import React, { useEffect, useState } from "react";
import {
  listRiskEvidence,
  listControlEvidence,
  deleteEvidence,
  restoreEvidence,
  downloadEvidenceUrl,
} from "../api/evidence.api.js";
import { hasModuleAccess } from "../accessControl";
import { useAuth } from "../useAuth";
import Button from "../ui/Button";
import StatusBadge from "../ui/StatusBadge";
import { colors, spacing, fontSizes } from "../ui/tokens";
import DestructiveConfirm from "./common/DestructiveConfirm";
import EvidenceDocumentViewer from "./EvidenceDocumentViewer";

const statusLabel = (status) => status?.replace("_", " ") || "";
const titleCase = (value) =>
  value
    ? value
        .toString()
        .split(/[_\s-]+/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
    : "";

const listStyle = {
  display: "grid",
  gap: spacing.md,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: spacing.lg,
  border: `1px solid rgba(107, 114, 128, 0.2)`,
  borderRadius: spacing.md,
  padding: spacing.lg,
  background: colors.background,
};

const metaStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: spacing.sm,
  color: colors.text,
  fontSize: fontSizes.md,
};

const actionsStyle = {
  display: "flex",
  gap: spacing.sm,
  alignItems: "center",
};

const mutedTextStyle = {
  color: colors.secondary,
  fontSize: fontSizes.sm,
};

export function EvidenceList({ parentType, parentId }) {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [viewerItem, setViewerItem] = useState(null);

  const isRisk = parentType === "risk";
  const moduleKey = isRisk ? "risks" : "controls";
  const canDelete = hasModuleAccess(user, moduleKey, "MANAGE");

  const load = async (cursor = null) => {
    setLoading(true);
    setError("");
    try {
      const fn = isRisk ? listRiskEvidence : listControlEvidence;
      const res = await fn(parentId, { cursor });
      const payload = res?.data ?? res;
      const nextItems = Array.isArray(payload?.items) ? payload.items : [];
      setItems((prev) => (cursor ? [...prev, ...nextItems] : nextItems));
      setNextCursor(payload?.next_cursor ?? null);
    } catch (err) {
      setError("Failed to load evidence.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentType, parentId]);

  const handleDeleteWithUndo = async (id) => {
    const prev = items;
    setItems((current) => current.filter((i) => i.id !== id));
    try {
      await deleteEvidence(id);
    } catch (err) {
      setItems(prev);
      setError("Delete failed.");
      return null;
    }
    return async () => {
      try {
        await restoreEvidence(id);
        await load(null);
      } catch (err) {
        setError("Restore failed.");
      }
    };
  };

  const confirmItem = Array.isArray(items) ? items.find((item) => item.id === confirmId) : null;

  return (
    <div style={listStyle}>
      {error && <div style={{ color: colors.danger }}>{error}</div>}
      {Array.isArray(items) && items.map((item) => (
        <div key={item.id} style={rowStyle}>
          <div style={metaStyle}>
            <StatusBadge
              variant="status"
              value={item.type}
              fallbackLabel={titleCase(item.type) || "Evidence"}
            />
            <StatusBadge
              variant="status"
              value={item.status}
              fallbackLabel={titleCase(statusLabel(item.status)) || "Unknown"}
            />
            {item.original_filename ? (
              <strong>{item.original_filename}</strong>
            ) : (
              <a href={item.url} target="_blank" rel="noreferrer">
                {item.url}
              </a>
            )}
          </div>
          <div style={actionsStyle}>
            {item.type === "file" && item.status === "active" ? (
              <>
                <Button variant="ghost" onClick={() => setViewerItem(item)}>
                  View
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.open(downloadEvidenceUrl(item.id), "_blank", "noopener,noreferrer")}
                >
                  Download
                </Button>
              </>
            ) : (
              <>
                {item.type === "url" && item.url ? (
                  <Button variant="secondary" onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}>
                    Open link
                  </Button>
                ) : (
                  <Button variant="secondary" disabled>
                    {item.status === "quarantined" ? "Quarantined" : "Not ready"}
                  </Button>
                )}
              </>
            )}
            {canDelete ? (
              <Button variant="destructive" onClick={() => setConfirmId(item.id)}>
                Delete
              </Button>
            ) : null}
          </div>
        </div>
      ))}
      {nextCursor && (
        <Button variant="secondary" onClick={() => load(nextCursor)} disabled={loading}>
          {loading ? "Loading..." : "Load more"}
        </Button>
      )}
      {loading && !items.length && <div style={mutedTextStyle}>Loading...</div>}
      {!loading && Array.isArray(items) && items.length === 0 && !error && (
        <div style={mutedTextStyle}>No Evidence recorded</div>
      )}
      <DestructiveConfirm
        actionId="EVIDENCE_DELETE"
        open={Boolean(confirmId)}
        resourceDisplayName={confirmItem?.original_filename || confirmItem?.url}
        context={{ cascadeSummary: "This document will become unavailable from every record that links to it." }}
        onCancel={() => setConfirmId(null)}
        onConfirm={async () => {
          const targetId = confirmId;
          setConfirmId(null);
          if (targetId) {
            return handleDeleteWithUndo(targetId);
          }
          return null;
        }}
      />
      <EvidenceDocumentViewer
        open={Boolean(viewerItem)}
        onClose={() => setViewerItem(null)}
        title={viewerItem?.original_filename || "Evidence document"}
        filename={viewerItem?.original_filename}
        mimeType={viewerItem?.mime_type}
        sizeBytes={viewerItem?.size_bytes}
        downloadUrl={viewerItem ? downloadEvidenceUrl(viewerItem.id) : null}
      />
    </div>
  );
}

export default EvidenceList;
